import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { sportInfo } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import PageShell from "../components/PageShell";
import BottomNav from "../components/BottomNav";
import MatchCard from "../components/MatchCard";
import EmptyState from "../components/EmptyState";
import { ArrowLeft, MapPin, Users as UsersIcon, Lock, Globe, Plus, Crown, Copy, Check, LogOut, Trash2 } from "lucide-react";

export default function TeamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/teams/${id}`);
      setData(data);
    } catch (err) {
      console.warn("[team-detail] load failed:", err?.message);
      toast.error("Team tidak ditemukan");
      navigate("/teams");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleJoin = async () => {
    setBusy(true);
    try {
      const body = data?.requires_invite ? { invite_code: inviteCode.trim().toUpperCase() } : {};
      await api.post(`/teams/${id}/join`, body);
      toast.success("Bergabung di team!");
      setShowInvite(false);
      setInviteCode("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal join");
    } finally { setBusy(false); }
  };

  const handleLeave = async () => {
    if (!window.confirm("Keluar dari team ini?")) return;
    setBusy(true);
    try {
      await api.post(`/teams/${id}/leave`);
      toast.success("Sudah keluar");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Hapus team ini? Tidak bisa di-undo.")) return;
    setBusy(true);
    try {
      await api.delete(`/teams/${id}`);
      toast.success("Team dihapus");
      navigate("/teams");
    } catch (e) {
      toast.error("Gagal hapus");
    } finally { setBusy(false); }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(data.team.invite_code);
      setCopied(true);
      toast.success("Kode tersalin!");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { toast.error("Gagal copy"); }
  };

  if (loading || !data) {
    return <PageShell><div className="px-4 py-10"><div className="h-40 rounded-2xl bg-card animate-pulse" /></div></PageShell>;
  }

  const t = data.team;
  const s = sportInfo(t.sport);

  if (data.requires_invite) {
    return (
      <PageShell hasBottomNav={false}>
        <header className="px-4 pt-6 pb-2 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
        </header>
        <div className="px-4 mt-10 flex flex-col items-center text-center gap-3">
          <div className="h-20 w-20 rounded-3xl bg-secondary border border-border flex items-center justify-center overflow-hidden">
            {t.logo ? <img src={t.logo} alt={t.name} className="w-full h-full object-cover" /> : <Lock className="h-8 w-8 text-muted-foreground" />}
          </div>
          <h1 className="font-heading text-2xl font-black tracking-tight">{t.name}</h1>
          <div className="text-sm text-muted-foreground">{t.description || "Team private — butuh kode undangan."}</div>
          <div className="w-full max-w-xs mt-4">
            <input
              data-testid="invite-code-input"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="MASUKKAN KODE"
              maxLength={12}
              className="w-full text-center bg-secondary border border-border rounded-2xl px-4 py-3.5 font-bold tracking-widest focus:outline-none focus:border-primary"
            />
            <button
              onClick={handleJoin}
              disabled={busy || !inviteCode}
              data-testid="join-with-code"
              className="mt-3 w-full px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-bold glow-cyan disabled:opacity-50"
            >
              {busy ? "Memproses..." : "Gabung"}
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <header className="px-4 pt-6 pb-2 flex items-center justify-between gap-3">
        <button onClick={() => navigate(-1)} className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        {data.is_owner && (
          <button onClick={handleDelete} className="h-10 w-10 rounded-full bg-destructive/10 border border-destructive/30 text-destructive flex items-center justify-center" data-testid="delete-team-btn">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </header>

      {/* Hero */}
      <section className="px-4 mt-4">
        <div className="bg-card border border-border rounded-3xl p-5 relative overflow-hidden">
          <div aria-hidden className="absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl opacity-30" style={{ background: s.color }} />
          <div className="flex items-center gap-4 relative">
            <div className="h-20 w-20 rounded-3xl overflow-hidden flex items-center justify-center shrink-0" style={{ background: s.color + "22", border: `1px solid ${s.color}66` }}>
              {t.logo ? <img src={t.logo} alt={t.name} className="w-full h-full object-cover" /> : <span className="text-4xl">{s.icon}</span>}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-xl font-black tracking-tight truncate">{t.name}</h1>
                {t.visibility === "private" ? <Lock className="h-3.5 w-3.5 text-muted-foreground" /> : <Globe className="h-3.5 w-3.5 text-muted-foreground" />}
              </div>
              <div className="mt-1 text-xs text-muted-foreground flex items-center gap-2">
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {t.city}</span>
                <span>·</span>
                <span>{s.label}</span>
              </div>
              {t.description && <div className="mt-2 text-xs text-muted-foreground/90 line-clamp-2">{t.description}</div>}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3 relative">
            <Stat label="Members" value={data.members.length} />
            <Stat label="Upcoming" value={data.upcoming.length} />
            <Stat label="Played" value={data.recent.length} />
          </div>

          {/* CTAs */}
          <div className="mt-4 flex gap-2 relative">
            {!data.is_member ? (
              <button
                onClick={handleJoin}
                disabled={busy}
                data-testid="join-team-btn"
                className="flex-1 px-5 py-3 rounded-full bg-primary text-primary-foreground font-bold glow-cyan active:scale-95"
              >
                {busy ? "Memproses..." : "Gabung Team"}
              </button>
            ) : (
              <button
                onClick={() => navigate(`/create?team_id=${t.team_id}`)}
                data-testid="create-match-from-team"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-primary text-primary-foreground font-bold glow-cyan active:scale-95"
              >
                <Plus className="h-4 w-4" /> Buat Match
              </button>
            )}
            {data.is_member && !data.is_owner && (
              <button onClick={handleLeave} disabled={busy} className="px-5 py-3 rounded-full bg-secondary border border-border text-foreground font-bold text-sm" data-testid="leave-team-btn">
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Invite code (members only, private team) */}
          {data.is_member && t.invite_code && (
            <button
              onClick={copyInvite}
              data-testid="copy-invite"
              className="mt-3 w-full bg-secondary/60 border border-border rounded-2xl px-4 py-2.5 flex items-center justify-between text-sm font-bold"
            >
              <span className="text-muted-foreground">Invite Code</span>
              <span className="inline-flex items-center gap-2 text-primary">
                {t.invite_code}
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </span>
            </button>
          )}
        </div>
      </section>

      {/* Members */}
      <section className="px-4 mt-6">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 px-1">Members</div>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {data.members.map((m) => (
            <div key={m.user_id} className="shrink-0 w-20 text-center" data-testid={`team-member-${m.user_id}`}>
              <div className="h-16 w-16 mx-auto rounded-full bg-secondary border border-border overflow-hidden">
                {m.picture ? <img src={m.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold">{(m.name || "?")[0]}</div>}
              </div>
              <div className="mt-1.5 text-[11px] font-bold truncate flex items-center justify-center gap-1">
                {m.user_id === t.owner_id && <Crown className="h-3 w-3 text-primary" />}
                <span className="truncate">{m.name?.split(" ")[0]}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Upcoming matches */}
      <section className="px-4 mt-6">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 px-1">Upcoming Matches</div>
        {data.upcoming.length === 0 ? (
          <EmptyState icon="📅" title="Belum ada match" body="Match tim akan muncul di sini." />
        ) : (
          <div className="space-y-3">
            {data.upcoming.map((m) => <MatchCard key={m.match_id} match={m} />)}
          </div>
        )}
      </section>

      {/* Recent matches */}
      {data.recent.length > 0 && (
        <section className="px-4 mt-6">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 px-1">Recent</div>
          <div className="space-y-3">
            {data.recent.map((m) => <MatchCard key={m.match_id} match={m} />)}
          </div>
        </section>
      )}

      <BottomNav />
    </PageShell>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-secondary/60 border border-border rounded-2xl py-2 text-center">
      <div className="font-heading text-lg font-black">{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}
